import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";

import {
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
} from "amazon-cognito-identity-js";
import { useTheme } from "../theming"; // Import from theming.js
import { userPool } from "../awsConfig";
import { getUserInfoFromToken, getTenantId } from "../utils/authUtils";
import {
  requestPresignedUrl,
  getLibraryCardIndexTags,
  updateLibraryCardRequest,
  getLibraryCardRequest,
} from "../actions/libraryCardActions";

const AuthForm = ({
  setIsAuthenticated,
  updateLibraryCardRequest,
  getLibraryCardRequest,
  setStartupRecord,
}) => {
  const [isRegister, setIsRegister] = useState(false); // Toggle between login and register
  const [isForgotPassword, setIsForgotPassword] = useState(false); // Toggle for forgot password
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    email: "",
    resetCode: "",
    newPassword: "",
  });
  const [message, setMessage] = useState(null);
  const [startupName, setStartupName] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showPasswords, setShowPasswords] = useState({});

  const navigate = useNavigate(); // Initialize navigate
  const theme = useTheme();

  const inputStyle = {
    backgroundColor: `${theme.primaryColor}10`,
    color: theme.textColor,
    borderColor: `${theme.primaryColor}40`,
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    // Clear validation errors when user starts typing
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  // Validation functions
  const validateForm = () => {
    const errors = {};
    const { username, password, email } = formData;

    // Username validation
    if (!username || username.length < 3) {
      errors.username = "Username must be at least 3 characters long";
    }

    // Password validation
    if (!password || password.length < 8) {
      errors.password = "Password must be at least 8 characters long";
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      errors.password =
        "Password must contain at least one uppercase letter, one lowercase letter, and one number";
    }

    // Email validation (only for registration)
    if (isRegister) {
      if (!email) {
        errors.email = "Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        errors.email = "Please enter a valid email address";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle Sign Up (User Registration)
  const handleSignUp = (e) => {
    e.preventDefault();

    // Clear previous messages and errors
    setMessage(null);
    setError(null);

    // Validate form before submission
    if (!validateForm()) {
      setMessage("Please fix the validation errors above");
      return;
    }

    setIsLoading(true);
    const { username, password, email } = formData;

    const attributeEmail = new CognitoUserAttribute({
      Name: "email",
      Value: email,
    });

    console.log("Attempting to register user:", { username, email });

    userPool.signUp(
      username,
      password,
      [attributeEmail],
      null,
      (err, result) => {
        setIsLoading(false);

        if (err) {
          console.error("Registration error:", err);

          // Handle specific Cognito errors
          let errorMessage = "Registration failed. ";

          switch (err.code) {
            case "UsernameExistsException":
              errorMessage +=
                "Username already exists. Please choose a different username.";
              break;
            case "InvalidPasswordException":
              errorMessage += "Password does not meet requirements.";
              break;
            case "InvalidParameterException":
              errorMessage += "Invalid input parameters.";
              break;
            case "TooManyRequestsException":
              errorMessage += "Too many requests. Please try again later.";
              break;
            default:
              errorMessage += err.message;
          }

          setMessage(errorMessage);
          return;
        }

        console.log("Registration successful:", result);
        setMessage(
          `Registration successful! Please check your email (${email}) for a confirmation code.`
        );

        // Store username for confirmation page
        localStorage.setItem("pendingUsername", username);

        // Redirect to confirmation page after a short delay
        setTimeout(() => {
          navigate("/confirm-signup");
        }, 2000);
      }
    );
  };

  // Handle Sign In (User Login)
  const handleSignIn = (e) => {
    e.preventDefault();
    const { username, password } = formData;

    const authenticationDetails = new AuthenticationDetails({
      Username: username,
      Password: password,
    });

    const userData = {
      Username: username,
      Pool: userPool,
    };

    const cognitoUser = new CognitoUser(userData);

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: async (result) => {
        // For AWS AppSync, we typically need the ID token (not access token)
        const idToken = result.getIdToken().getJwtToken();
        const accessToken = result.getAccessToken().getJwtToken();
        
        // Store ID token for AppSync authentication (primary)
        localStorage.setItem("jwtToken", idToken);
        // Also store access token in case we need it later
        localStorage.setItem("accessToken", accessToken);
        
        console.log("AuthForm - Stored ID token for AppSync authentication");

        setMessage("Login successful!");
        setIsAuthenticated(true); // Set user as authenticated

        // Phase 2 — tenantId migration: tenantId is now derived from JWT sub (see getTenantId()).
        // No GSI lookup or startup name required to navigate to dashboard.
        const storedStartup = localStorage.getItem("STARTUP"); // display name only — may be null

        // Check for a pending plan change the creator selected on the plans page
        // before being redirected here to log in (e.g. on a fresh page load).
        const pendingPlan = localStorage.getItem("SELECTED_PLAN");
        if (pendingPlan) {
          console.log(
            `PMM - AuthForm - pending plan change detected: ${pendingPlan}`
          );
          const PLAN_PRICE_IDS = {
            starter: "price_1T6KoAJI72p7gLB6GJJHU69",
            professional: "price_1T6KouJI72p7gLB6mVLhFZLn",
            business: "price_1T6KpiJI72p7gLB6tvLi4Ssa",
          };
          const priceId = PLAN_PRICE_IDS[pendingPlan];
          if (priceId) {
            // Paid plan — redirect to Stripe Checkout
            localStorage.removeItem("SELECTED_PLAN");
            const userInfo = getUserInfoFromToken();
            const stripeApiUrl = process.env.REACT_APP_STRIPE_API_URL;
            try {
              const response = await fetch(`${stripeApiUrl}/stripe/checkout`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  priceId,
                  customerEmail: userInfo?.email,
                  startupName: storedStartup || userInfo?.username,
                  userId: userInfo?.userId || "",
                }),
              });
              const data = await response.json();
              if (data.url) {
                window.location.href = data.url;
                return;
              }
            } catch (err) {
              console.error("PMM - Stripe checkout error after login:", err);
            }
          } else {
            // Free or top-up — save via Redux and go to dashboard
            const userInfo = getUserInfoFromToken();
            const tenantId = getTenantId();
            const startupJson = {
              tenantId,
              entityType: "STARTUPRECORD",
              operationType: "SAVE_LIBRARY_CARD",
              payload: JSON.stringify({
                startup: storedStartup || userInfo?.username,
                ...(userInfo?.userId && { createdBy: userInfo.userId }),
                ...(userInfo?.email && { ownerEmail: userInfo.email }),
                planName: pendingPlan,
              }),
            };
            updateLibraryCardRequest(startupJson);
            localStorage.removeItem("SELECTED_PLAN");
          }
        }
        console.log("PMM - AuthForm - login complete, navigating to dashboard");
        navigate("/");
      },
      onFailure: (err) => {
        setMessage(`Error logging in: ${err.message}`);
      },
    });
  };

  // Handle Forgot Password - Send Reset Code
  const handleForgotPassword = (e) => {
    e.preventDefault();
    const { username } = formData;

    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: userPool,
    });

    cognitoUser.forgotPassword({
      onSuccess: (data) => {
        setMessage("Password reset code sent to email.");
        setIsForgotPassword(true); // Show reset password form
      },
      onFailure: (err) => {
        setMessage(`Error sending reset code: ${err.message}`);
      },
    });
  };

  // Handle Reset Password
  const handleResetPassword = (e) => {
    e.preventDefault();
    const { username, resetCode, newPassword } = formData;

    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: userPool,
    });

    cognitoUser.confirmPassword(resetCode, newPassword, {
      onSuccess: () => {
        setMessage("Password reset successful! You can now log in.");
        setIsForgotPassword(false); // Go back to login form
      },
      onFailure: (err) => {
        setMessage(`Error resetting password: ${err.message}`);
      },
    });
  };

  // Handle Resend Reset Code
  const handleResendCode = () => {
    if (!formData.username) {
      setMessage("Please enter your username first.");
      return;
    }
    const cognitoUser = new CognitoUser({ Username: formData.username, Pool: userPool });
    cognitoUser.forgotPassword({
      onSuccess: () => setMessage("A new code has been sent to your email."),
      onFailure: (err) => setMessage(`Error: ${err.message}`),
    });
  };

  // Toggle between Login and Register forms
  const toggleForm = () => {
    setIsRegister(!isRegister);
    setIsForgotPassword(false); // Hide forgot password form when switching
    setMessage(null); // Clear previous messages
  };

  // const handleUpdateStartup = (startup) => {
  //   if (!startup) {
  //     setError("Please enter a startup name");
  //     return;
  //   }
  //   setError(null);

  //   const payload = { startup };

  //   const startupJson = {
  //     tenantId: startup,
  //     entityType: "STARTUPRECORD",
  //     operationType: "SAVE_LIBRARY_CARD",
  //     // payload: JSON.stringify(payload),
  //   };

  //   // updateLibraryCardRequest(startupJson);
  //   navigate("/");
  // };

  const handleCancel = () => {
    setIsModalOpen(false); // Close the modal without saving
  };

  const toggleShow = (field) =>
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));

  const EyeIcon = ({ field }) => (
    <button
      type="button"
      onClick={() => toggleShow(field)}
      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none bg-transparent border-0 p-0 cursor-pointer"
      tabIndex={-1}
    >
      {showPasswords[field] ? (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
        </svg>
      ) : (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      )}
    </button>
  );

  return (
    <div
      className="flex flex-col items-center justify-center min-h-screen bg-background"
      style={{
        backgroundColor: theme.backgroundColor,
        color: theme.textColor,
      }}
    >
      <div
        className="w-full max-w-md p-8 space-y-6 rounded-lg"
        style={{
          color: theme.primaryColor,
          backgroundColor: theme.backgroundColor,
          border: `1px solid ${theme.primaryColor}20`,
          boxShadow: `0 8px 32px ${theme.primaryColor}20`,
        }}
      >
        <h2
          className="text-3xl font-extrabold text-center text-accent"
          style={{
            // backgroundColor: theme.accentColor,
            color: theme.accentColor,
          }}
        >
          {isRegister
            ? "Register"
            : isForgotPassword
            ? "Reset Password"
            : "Login"}
        </h2>

        {/* If forgot password form is active */}
        {isForgotPassword ? (
          <form onSubmit={handleResetPassword} className="space-y-6">
            <div>
              <label
                htmlFor="username"
                className="block text-sm font-medium "
              >
                Username
              </label>
              <input
                style={inputStyle}
                type="text"
                name="username"
                id="username"
                value={formData.username}
                onChange={handleChange}
                className="w-full px-3 py-2 mt-1 border rounded-md focus:ring-accent focus:border-accent"
                required
              />
            </div>
            <div>
              <label
                htmlFor="resetCode"
                className="block text-sm font-medium "
              >
                Reset Code
              </label>
              <input
                style={inputStyle}
                type="text"
                name="resetCode"
                id="resetCode"
                value={formData.resetCode}
                onChange={handleChange}
                className="w-full px-3 py-2 mt-1 border rounded-md focus:ring-accent focus:border-accent"
                required
              />
            </div>
            <div>
              <label
                htmlFor="newPassword"
                className="block text-sm font-medium "
              >
                New Password
              </label>
              <div className="relative">
                <input
                  style={inputStyle}
                  type={showPasswords.newPassword ? "text" : "password"}
                  name="newPassword"
                  id="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  className="w-full px-3 py-2 mt-1 border rounded-md focus:ring-accent focus:border-accent pr-10"
                  required
                />
                <EyeIcon field="newPassword" />
              </div>
            </div>
            <button
              type="submit"
              className="w-full px-4 py-2 font-semibold text-white bg-accent rounded-md hover:bg-accent-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
            >
              Reset Password
            </button>
            <div className="text-center">
              <button
                type="button"
                onClick={handleResendCode}
                className="text-sm underline"
                style={{ color: theme.accentColor, backgroundColor: 'transparent' }}
              >
                Didn't receive a code? Resend
              </button>
            </div>
          </form>
        ) : (
          <form
            onSubmit={isRegister ? handleSignUp : handleSignIn}
            className="space-y-6"
          >
            {/* Common fields for login and register */}
            <div>
              <label
                htmlFor="username"
                className="block text-sm font-medium "
              >
                Username
              </label>
              <input
                style={inputStyle}
                type="text"
                name="username"
                id="username"
                value={formData.username}
                onChange={handleChange}
                className={`w-full px-3 py-2 mt-1 border rounded-md focus:ring-accent focus:border-accent ${
                  validationErrors.username ? "border-red-500" : ""
                }`}
                required
              />
              {validationErrors.username && (
                <p className="text-red-500 text-sm mt-1">
                  {validationErrors.username}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium "
              >
                Password
              </label>
              <div className="relative">
                <input
                  style={inputStyle}
                  type={showPasswords.password ? "text" : "password"}
                  name="password"
                  id="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 mt-1 border rounded-md focus:ring-accent focus:border-accent pr-10 ${
                    validationErrors.password ? "border-red-500" : ""
                  }`}
                  required
                />
                <EyeIcon field="password" />
              </div>
              {validationErrors.password && (
                <p className="text-red-500 text-sm mt-1">
                  {validationErrors.password}
                </p>
              )}
            </div>
            {isRegister && (
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium "
                >
                  Email
                </label>
                <input
                  style={inputStyle}
                  type="email"
                  name="email"
                  id="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 mt-1 border rounded-md focus:ring-accent focus:border-accent ${
                    validationErrors.email ? "border-red-500" : ""
                  }`}
                  required
                />
                {validationErrors.email && (
                  <p className="text-red-500 text-sm mt-1">
                    {validationErrors.email}
                  </p>
                )}
              </div>
            )}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full px-4 py-2 font-semibold text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent ${
                  isLoading
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-accent-dark"
                }`}
                style={{
                  backgroundColor: theme.accentColor,
                  color: theme.buttonTextColor,
                }}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {isRegister ? "Registering..." : "Logging in..."}
                  </span>
                ) : isRegister ? (
                  "Register"
                ) : (
                  "Login"
                )}
              </button>
            </div>
          </form>
        )}

        {message && <p className="text-sm text-red-600">{message}</p>}

        {!isForgotPassword && !isRegister && (
          <div className="text-center">
            <button
              onClick={() => { setIsForgotPassword(true); setMessage(null); }}
              className="text-sm underline bg-transparent border-0 p-0 cursor-pointer"
              style={{ color: theme.accentColor, backgroundColor: 'transparent' }}
            >
              Forgot password?
            </button>
          </div>
        )}

        <div className="text-center">
          <button
            onClick={toggleForm}
            className="text-sm underline bg-transparent border-0 p-0 cursor-pointer"
            style={{ color: theme.accentColor, backgroundColor: 'transparent' }}
          >
            {isRegister
              ? "Already have an account? Login"
              : "Don't have an account? Register"}
          </button>
        </div>
      </div>
      {/* Modal for capturing the startup name */}
      {/* <Modal
        title="Enter Your Startup Name"
        open={isModalOpen} // Use the open prop to control visibility
        onOk={handleUpdateStartup}
        onCancel={handleCancel}
      >
        <Input
          placeholder="Startup Name"
          value={startupName}
          onChange={(e) => setStartupName(e.target.value)} // Update the state with input value
        />
      </Modal> */}
    </div>
  );
};

AuthForm.propTypes = {
  updateLibraryCardRequest: PropTypes.func.isRequired,
  getLibraryCardRequest: PropTypes.func.isRequired,
};

/** **mapStateToProps Function** */
const mapStateToProps = (state) => ({});

/** **mapDispatchToProps Function** */
const mapDispatchToProps = (dispatch) => ({
  updateLibraryCardRequest: (payload) =>
    dispatch(updateLibraryCardRequest(payload)),
  getLibraryCardRequest: (payload) => dispatch(getLibraryCardRequest(payload)),
});

/** **Export Connected DynamicGrid Component** */
export default connect(mapStateToProps, mapDispatchToProps)(AuthForm);
